import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../lib/api.js";

/**
 * Webhook endpoints and their delivery log — docs/03-API-SPEC.md §Webhooks.
 *
 * The replay button is the point of this screen. A webhook that failed five times over twelve hours
 * is not an error to read about; it is something someone needs to be able to re-fire once the
 * receiving system is back, without a developer and a redis-cli.
 */

interface Delivery {
  id: string;
  event: string;
  statusCode: number | null;
  attempts: number;
  succeededAt: string | null;
  nextRetryAt: string | null;
  createdAt: string;
}

interface Endpoint {
  id: string;
  url: string;
  events: string[];
  isActive: boolean;
  createdAt: string;
  deliveries: Delivery[];
}

const AVAILABLE_EVENTS = [
  "submission.created",
  "submission.updated",
  "form.opened",
  "form.closed",
  "duplicate.flagged",
];

const deliveryStatus = (delivery: Delivery): { label: string; className: string } => {
  if (delivery.succeededAt) return { label: "Delivered", className: "text-primary" };
  if (delivery.nextRetryAt) return { label: "Retrying", className: "text-muted-foreground" };
  return { label: "Failed", className: "text-brand-red" };
};

export const WebhooksPanel = ({ formId }: { formId: string }): JSX.Element => {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>(["submission.created"]);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["webhooks", formId] });

  const query = useQuery({
    queryKey: ["webhooks", formId],
    queryFn: () => apiGet<{ data: Endpoint[] }>(`/forms/${formId}/webhooks`),
  });

  const create = useMutation({
    mutationFn: () => apiPost<{ secret: string }>(`/forms/${formId}/webhooks`, { url, events }),
    onSuccess: async (result) => {
      setNewSecret(result.secret);
      setUrl("");
      await invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/forms/${formId}/webhooks/${id}`),
    onSuccess: invalidate,
  });

  const replay = useMutation({
    mutationFn: (input: { endpointId: string; deliveryId: string }) =>
      apiPost(
        `/forms/${formId}/webhooks/${input.endpointId}/deliveries/${input.deliveryId}/replay`,
      ),
    onSuccess: invalidate,
  });

  const endpoints = query.data?.data ?? [];

  return (
    <div className="space-y-4">
      {newSecret && (
        <div className="rounded-lg border border-accent bg-accent/10 p-3">
          <p className="text-sm font-medium">Signing secret — shown once</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Copy it now. It is not stored anywhere you can read it again. The receiving system
            verifies the <code>X-SGA-Signature</code> header with it.
          </p>
          <code className="mt-2 block break-all rounded bg-card p-2 font-mono text-xs">
            {newSecret}
          </code>
          <button
            type="button"
            onClick={() => setNewSecret(null)}
            className="mt-2 text-xs underline"
          >
            I have copied it
          </button>
        </div>
      )}

      <section className="space-y-2 rounded-lg border bg-card p-3">
        <h4 className="font-heading text-sm font-semibold">Add an endpoint</h4>
        <input
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://example.org/hooks/sga"
          className="w-full rounded border px-2 py-1.5 text-sm"
        />
        <div className="flex flex-wrap gap-2">
          {AVAILABLE_EVENTS.map((event) => (
            <button
              key={event}
              type="button"
              onClick={() =>
                setEvents((current) =>
                  current.includes(event)
                    ? current.filter((item) => item !== event)
                    : [...current, event],
                )
              }
              className={`rounded-full border px-3 py-1 text-xs ${
                events.includes(event)
                  ? "border-primary bg-primary text-primary-foreground"
                  : "hover:bg-secondary"
              }`}
            >
              {event}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={!url.startsWith("https://") || events.length === 0 || create.isPending}
          onClick={() => create.mutate()}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          {create.isPending ? "Creating…" : "Create endpoint"}
        </button>
        {create.isError && <p className="text-xs text-brand-red">{String(create.error)}</p>}
      </section>

      {endpoints.length === 0 ? (
        <p className="text-xs text-muted-foreground">No webhook endpoints on this form.</p>
      ) : (
        endpoints.map((endpoint) => (
          <section key={endpoint.id} className="rounded-lg border bg-card">
            <header className="flex items-center justify-between gap-3 border-b px-3 py-2">
              <div className="min-w-0">
                <p className="truncate font-mono text-xs">{endpoint.url}</p>
                <p className="text-[11px] text-muted-foreground">{endpoint.events.join(", ")}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] ${
                    endpoint.isActive ? "bg-accent/20 text-primary" : "bg-muted"
                  }`}
                >
                  {endpoint.isActive ? "Active" : "Paused"}
                </span>
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === endpoint.id ? null : endpoint.id)}
                  className="rounded border px-2 py-1 text-[11px] hover:bg-secondary"
                >
                  {expanded === endpoint.id ? "Hide" : `Deliveries (${endpoint.deliveries.length})`}
                </button>
                <button
                  type="button"
                  onClick={() => remove.mutate(endpoint.id)}
                  className="rounded border border-brand-red px-2 py-1 text-[11px] text-brand-red hover:bg-brand-red/5"
                >
                  Delete
                </button>
              </div>
            </header>

            {expanded === endpoint.id && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="px-3 py-1.5 font-normal">Event</th>
                      <th className="px-3 py-1.5 font-normal">Status</th>
                      <th className="px-3 py-1.5 font-normal">HTTP</th>
                      <th className="px-3 py-1.5 font-normal">Attempts</th>
                      <th className="px-3 py-1.5 font-normal">When</th>
                      <th className="px-3 py-1.5 font-normal"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {endpoint.deliveries.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-3 text-muted-foreground">
                          Nothing delivered yet.
                        </td>
                      </tr>
                    )}
                    {endpoint.deliveries.map((delivery) => {
                      const status = deliveryStatus(delivery);
                      return (
                        <tr key={delivery.id}>
                          <td className="px-3 py-1.5 font-mono">{delivery.event}</td>
                          <td className={`px-3 py-1.5 ${status.className}`}>{status.label}</td>
                          <td className="px-3 py-1.5">{delivery.statusCode ?? "—"}</td>
                          <td className="px-3 py-1.5">{delivery.attempts}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">
                            {new Date(delivery.createdAt).toLocaleString("en-GB", {
                              timeZone: "Africa/Lagos",
                            })}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <button
                              type="button"
                              disabled={replay.isPending}
                              onClick={() =>
                                replay.mutate({
                                  endpointId: endpoint.id,
                                  deliveryId: delivery.id,
                                })
                              }
                              className="rounded border px-2 py-0.5 text-[11px] hover:bg-secondary disabled:opacity-50"
                            >
                              Replay
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))
      )}
    </div>
  );
};
